import React, { useCallback, useMemo, useState, forwardRef, useImperativeHandle } from 'react'
import { CellInfo } from 'react-table'
import { t } from 'ttag'
import { ReactComponent as IconGoalLine } from '../../assets/v2/icons/icon-goal-line.svg'
import { ReactComponent as IconGoalLineInverse } from '../../assets/v2/icons/icon-goal-line-inverse.svg'
import { ProgressCell } from '../Table/components/ProgressCell/ProgressCell'
import { Table } from '../Table/Table'
import {
  ActivityProgress,
  CountryActivityProgress,
  DivisionActivityProgress,
  GoalWithProgress,
  GroupView,
  ProgressType,
  UserCompanyDto,
  UserProfileDto,
} from '../../services/api.types'
import { ActivityView } from '../../views/Activity/store/ActivityReducer'
import BlueTooltip from '../../components/YumBlueTooltip/YumBlueTooltip'
import { countryToFlag, countryWithFlag } from '../YumCountrySelect/YumCountrySelect'
import { getFullName, formatThousands } from '../../common/helpers/translations'
import { isCollectiveType, isCustomType, isPersistenceType } from '../../common/helpers/challenges'
import {
  getActivityProgressCaloriesComparator,
  getActivityProgressMinutesComparator,
  getActivityProgressPercentageComparator,
  getActivityProgressPersistenceComparator,
  getActivityProgressStepsComparator,
  getCountryActivityProgressCaloriesComparator,
  getCountryActivityProgressMinutesComparator,
  getCountryActivityProgressPercentageComparator,
  getCountryActivityProgressPersistenceComparator,
  getCountryActivityProgressStepsComparator,
  getDivisionActivityProgressCaloriesComparator,
  getDivisionActivityProgressMinutesComparator,
  getDivisionActivityProgressPercentageComparator,
  getDivisionActivityProgressPersistenceComparator,
  getDivisionActivityProgressStepsComparator,
} from '../../common/helpers/comparators'
import { isNative } from '../../common/helpers/isNative'

export const ROWS_VISIBLE_BY_DEFAULT = isNative() ? 100 : 1000

interface IYumLegacyGoalRankingTableProps {
  goalDto: GoalWithProgress
  activityProgress: ActivityProgress[]
  divisionActivityProgress: DivisionActivityProgress[]
  countryActivityProgress: CountryActivityProgress[]
  activityProgressLoading: boolean
  activityView: ActivityView
  groupView: GroupView
  userDto: UserProfileDto
  userCompanies: UserCompanyDto[]
  companyHasCountries: boolean
  activityPeriodPercentage: number
  /** Whether to use the 2026 layout refresh styling */
  compactLayout?: boolean
}

export interface YumLegacyGoalRankingTableHandle {
  findMe: () => void
}

export const YumLegacyGoalRankingTable = forwardRef<YumLegacyGoalRankingTableHandle, IYumLegacyGoalRankingTableProps>(
  (
    {
      goalDto,
      activityProgress,
      divisionActivityProgress,
      countryActivityProgress,
      activityProgressLoading,
      activityView,
      groupView,
      userDto,
      userCompanies,
      activityPeriodPercentage,
      companyHasCountries,
    },
    ref,
  ) => {
    const [visibleItems, setVisibleItems] = useState(ROWS_VISIBLE_BY_DEFAULT)

    // Memoize user company lookup
    const userCompany = useMemo(() => {
      return (
        userCompanies.find((uc: UserCompanyDto) => uc.companyId === goalDto.companyId) || {
          divisionId: '',
          country: '',
        }
      )
    }, [userCompanies, goalDto.companyId])

    // Memoize activity type flags
    const activityTypeFlags = useMemo(() => {
      const { progressType } = goalDto
      return {
        isCollectiveChallenge: isCollectiveType(progressType),
        isPersistenceChallenge: isPersistenceType(progressType),
        isCustom: isCustomType(progressType),
        isSteps: [ProgressType.STEPS, ProgressType.COLLECTIVE_STEPS].includes(progressType),
        isCalories: [ProgressType.CALORIES, ProgressType.COLLECTIVE_CALORIES].includes(progressType),
        isMinutes: [ProgressType.INTENSIVE_MINUTES, ProgressType.COLLECTIVE_INTENSIVE_MINUTES].includes(progressType),
      }
    }, [goalDto.progressType])

    // Memoize activity progress data and comparator
    const { activityProgressData, activityProgressComparator } = useMemo<{
      activityProgressData: ActivityProgress[] | DivisionActivityProgress[] | CountryActivityProgress[]
      activityProgressComparator: (a: [string, any], b: [string, any]) => number
    }>(() => {
      const { progressType } = goalDto
      const { isSteps, isCalories, isMinutes, isPersistenceChallenge } = activityTypeFlags

      if (activityView === ActivityView.GROUP) {
        return {
          activityProgressData: divisionActivityProgress,
          activityProgressComparator:
            isPersistenceChallenge || isCustomType(progressType)
              ? getDivisionActivityProgressPersistenceComparator
              : isSteps
                ? getDivisionActivityProgressStepsComparator
                : isCalories
                  ? getDivisionActivityProgressCaloriesComparator
                  : isMinutes
                    ? getDivisionActivityProgressMinutesComparator
                    : getDivisionActivityProgressPercentageComparator,
        }
      } else if (activityView === ActivityView.COUNTRY) {
        return {
          activityProgressData: countryActivityProgress,
          activityProgressComparator:
            isPersistenceChallenge || isCustomType(progressType)
              ? getCountryActivityProgressPersistenceComparator
              : isSteps
                ? getCountryActivityProgressStepsComparator
                : isCalories
                  ? getCountryActivityProgressCaloriesComparator
                  : isMinutes
                    ? getCountryActivityProgressMinutesComparator
                    : getCountryActivityProgressPercentageComparator,
        }
      } else {
        return {
          activityProgressData: activityProgress,
          activityProgressComparator:
            isPersistenceChallenge || isCustomType(progressType)
              ? getActivityProgressPersistenceComparator
              : isSteps
                ? getActivityProgressStepsComparator
                : isCalories
                  ? getActivityProgressCaloriesComparator
                  : isMinutes
                    ? getActivityProgressMinutesComparator
                    : getActivityProgressPercentageComparator,
        }
      }
    }, [
      activityView,
      goalDto.progressType,
      activityTypeFlags,
      activityProgress,
      divisionActivityProgress,
      countryActivityProgress,
    ])

    // Memoize sorted and processed data
    const data = useMemo(() => {
      const activityProgressSorted = Object.entries(activityProgressData || {}).sort(activityProgressComparator) || []
      const { userId } = userDto
      const { divisionId, country } = userCompany
      const { id: goalId, progressType, activityFormulaTarget, customType, customUnit } = goalDto
      const { isCollectiveChallenge, isPersistenceChallenge } = activityTypeFlags

      const data = activityProgressSorted
        .map((i, index) => {
          if (!i || !i[1]) return null

          const { totalPercentage } = i[1]
          let name, compareId, ofPeriodPassedPercentage, isSelf

          if (activityView === ActivityView.INDIVIDUAL) {
            const { firstName, lastName, email } = i[1]
            compareId = i[1].userId
            name = getFullName(firstName, lastName, email)
            isSelf = userId === compareId
            ofPeriodPassedPercentage = i[1].ofPeriodPassedPercentage
          } else if (activityView === ActivityView.GROUP) {
            name = i[1].divisionName
            compareId = i[1].divisionId
            ofPeriodPassedPercentage = activityPeriodPercentage
            if (groupView === GroupView.COUNTRIES_MERGED) {
              isSelf = divisionId === compareId
            } else {
              isSelf = divisionId === compareId && country === i[1].country
            }
          } else {
            compareId = i[1].country
            name = countryWithFlag(compareId)
            isSelf = country === compareId
            ofPeriodPassedPercentage = activityPeriodPercentage
          }

          let target = 100

          // Needed for past goals
          if (ofPeriodPassedPercentage === null && i[1].endDate < new Date().getTime()) {
            ofPeriodPassedPercentage = target
          }

          // Custom target
          if (progressType === ProgressType.ALL && activityFormulaTarget && ofPeriodPassedPercentage !== null) {
            target = activityFormulaTarget
            ofPeriodPassedPercentage = Math.round(ofPeriodPassedPercentage * (target / 100))
          }

          const isCompleted = totalPercentage >= target
          const rank = index + 1

          const prevUser = activityProgressSorted[index - 1]?.[1] || { totalPercentage: undefined }
          const nextUser = activityProgressSorted[index + 1]?.[1] || { totalPercentage: undefined }

          let isSeparatorForAbove, isSeparatorForBelow
          if (
            !isCollectiveChallenge &&
            (ofPeriodPassedPercentage || ofPeriodPassedPercentage === 0) &&
            activityView === ActivityView.INDIVIDUAL
          ) {
            isSeparatorForAbove =
              isTotalProjectedPercentageVisible &&
              totalPercentage < ofPeriodPassedPercentage &&
              prevUser.totalPercentage === undefined

            isSeparatorForBelow =
              isTotalProjectedPercentageVisible &&
              totalPercentage >= ofPeriodPassedPercentage &&
              (!nextUser.totalPercentage || nextUser.totalPercentage < ofPeriodPassedPercentage)
          }

          // Prevent multiple green lines – side effect with 0%
          if (isSeparatorForBelow && ofPeriodPassedPercentage === 0) {
            isSeparatorForBelow = false
          }

          return {
            ...i[1],
            name,
            isCompleted,
            isSelf,
            isSeparatorForBelow,
            isSeparatorForAbove,
            rank,
            progressType,
            target,
            alignRight: isCollectiveChallenge,
          }
        })
        .filter(Boolean)

      // We need the isBeforeSelf flag to prevent table line in the previous row.
      if (data.length) {
        data.some((element, index, array) => {
          if (element.isSelf && index > 0) {
            array[index - 1].isBeforeSelf = true
            return true
          }
          return false
        })
      }

      return data
    }, [
      activityProgressData,
      activityProgressComparator,
      userDto,
      userCompany,
      goalDto,
      activityView,
      groupView,
      activityPeriodPercentage,
      activityTypeFlags,
    ])

    // Memoize table columns
    const columns = useMemo(() => {
      const { id: goalId, companyId } = goalDto
      const isMciPeople =
        companyId === '88e7ce47-96ee-4679-b340-72ba58835e41' &&
        goalDto.progressType === ProgressType.COLLECTIVE_CALORIES &&
        activityView === ActivityView.INDIVIDUAL

      return [
        {
          className: Table.rankBEM(data.length),
          headerClassName: Table.rankBEM(data.length),
          Cell: ({ original: { rank } }: CellInfo) => <span>{rank + '.'}</span>,
          accessor: 'rank',
        },
        {
          Cell: ({ original }: CellInfo) => {
            if (activityView === ActivityView.INDIVIDUAL) {
              const goalIconStyles = original.isSeparatorForAbove
                ? { position: 'absolute', zIndex: 2, top: 2, left: '50%', marginLeft: -9 }
                : { position: 'absolute', zIndex: 2, bottom: -8, left: '50%', marginLeft: -9 }
              return (
                <>
                  {original.isSeparatorForAbove || original.isSeparatorForBelow ? (
                    <BlueTooltip
                      title={t`Being above the line shows that you are on the track to complete the goal by the end of the period.`}
                      placement="top"
                      enterTouchDelay={50}
                      leaveTouchDelay={3000}
                      arrow
                      PopperProps={{
                        container: document.getElementById('content'),
                        disablePortal: true,
                      }}
                    >
                      <span style={goalIconStyles as object}>
                        {original.isSeparatorForBelow ? <IconGoalLine /> : <IconGoalLineInverse />}
                      </span>
                    </BlueTooltip>
                  ) : null}
                  <span>
                    <span
                      id={goalId + original.userId}
                      style={{
                        display: 'inline-block',
                        verticalAlign: 'middle',
                        overflow: 'hidden',
                      }}
                    >
                      {original.name}
                    </span>
                    {original.country || original.divisionName ? (
                      <>
                        <br />
                        <small>
                          {!isMciPeople && original.country && companyHasCountries ? (
                            <span style={{ marginRight: 2, lineHeight: 1 }}>{countryToFlag(original.country)}</span>
                          ) : null}
                          {original.divisionName}
                        </small>
                      </>
                    ) : null}
                  </span>
                </>
              )
            } else if (activityView === ActivityView.GROUP) {
              return (
                <span>
                  <span id={goalId + original.divisionId}>
                    {original.country && companyHasCountries ? (
                      <span style={{ marginRight: 2, lineHeight: 1 }}>{countryToFlag(original.country)}</span>
                    ) : null}
                    {original.divisionName}
                  </span>
                  <br />
                  <small style={{ whiteSpace: 'nowrap' }}>
                    {t`Participants`}: {formatThousands(original.participants)}
                  </small>
                </span>
              )
            } else {
              return (
                <span>
                  <span id={goalId + original.country}>{countryWithFlag(original.country)}</span>
                  <br />
                  <small style={{ whiteSpace: 'nowrap' }}>
                    {t`Participants`}: {formatThousands(original.participants)}
                  </small>
                </span>
              )
            }
          },
          Header: t`Name`,
          accessor: 'name',
        },
        {
          Cell: ({
            original: {
              caloriesTotal,
              intensiveMinutesTotal,
              stepsTotal,
              totalPercentage,
              progressType,
              target,
              persistenceTotal,
              persistencePercentage,
              country,
            },
          }: CellInfo) => {
            if (isMciPeople) {
              return country && companyHasCountries ? countryToFlag(country) : ''
            } else {
              return (
                <ProgressCell
                  caloriesTotal={caloriesTotal}
                  intensiveMinutesTotal={intensiveMinutesTotal}
                  stepsTotal={stepsTotal}
                  progress={
                    activityTypeFlags.isPersistenceChallenge
                      ? persistencePercentage
                      : activityTypeFlags.isCollectiveChallenge
                        ? undefined
                        : parseInt(totalPercentage)
                  }
                  progressType={progressType}
                  activityFormulaTarget={target}
                  persistenceTotal={persistenceTotal}
                  customType={goalDto.customType}
                  customUnit={goalDto.customUnit}
                  activityView={activityView}
                />
              )
            }
          },
          Header: t`Result`,
          accessor: 'totalPercentage',
        },
      ]
    }, [data.length, activityView, activityTypeFlags, goalDto, companyHasCountries])

    const fetchMoreData = useCallback(
      (totalItems: number) => () => {
        if (visibleItems < totalItems) {
          setVisibleItems(visibleItems + ROWS_VISIBLE_BY_DEFAULT)
        }
      },
      [visibleItems],
    )

    const scrollToSelf = useCallback(
      (goalId: string, activityView: ActivityView, userId: string, divisionId: string, country: string) => {
        setTimeout(() => {
          let compareId
          if (activityView === ActivityView.INDIVIDUAL) {
            compareId = userId
          } else if (activityView === ActivityView.GROUP) {
            compareId = divisionId
          } else {
            compareId = country
          }

          const element = document.getElementById(String(goalId) + String(compareId))
          if (element) {
            if (isNative()) {
              const top = element.getBoundingClientRect().top + window.pageYOffset - window.innerHeight / 2
              document.getElementById('content')?.scrollTo({ top, behavior: 'smooth' })
            } else {
              element.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' })
            }
          }
        }, 100)
      },
      [],
    )

    const findMe = useCallback(() => {
      const { id: goalId } = goalDto
      const { userId } = userDto
      const { divisionId, country } = userCompany

      // Calculate self position
      let selfPosition = 0
      if (activityView === ActivityView.INDIVIDUAL) {
        selfPosition = data.findIndex((progress) => progress?.userId === userId) + 1
      } else if (activityView === ActivityView.GROUP) {
        if (groupView === GroupView.COUNTRIES_MERGED) {
          selfPosition = data.findIndex((progress) => progress?.divisionId === divisionId) + 1
        } else {
          selfPosition =
            data.findIndex((progress) => progress?.divisionId === divisionId && progress?.country === country) + 1
        }
      } else {
        selfPosition = data.findIndex((progress) => progress?.country === country) + 1
      }

      // If "me" is not visible, load at least that many items
      if (visibleItems < selfPosition) {
        const newVisibleItems =
          Math.ceil(selfPosition / ROWS_VISIBLE_BY_DEFAULT) * ROWS_VISIBLE_BY_DEFAULT + ROWS_VISIBLE_BY_DEFAULT
        setVisibleItems(newVisibleItems)
        scrollToSelf(goalId, activityView, userId, divisionId || '', country)
      } else {
        scrollToSelf(goalId, activityView, userId, divisionId || '', country)
      }
    }, [goalDto, userDto, userCompany, activityView, groupView, data, visibleItems, scrollToSelf])

    // Expose findMe method through ref
    useImperativeHandle(
      ref,
      () => ({
        findMe,
      }),
      [findMe],
    )

    const visibleData = data.slice(0, visibleItems)
    const totalItems = data.length

    return (
      <>
        <Table
          data={visibleData}
          columns={columns}
          isLoading={!visibleData.length && activityProgressLoading}
          defaultSorted={[{ id: 'rank', desc: false }]}
          fetchMoreData={fetchMoreData(totalItems)}
          className="activity-list"
        />
        {data.length === 0 && !activityProgressLoading ? (
          <div className="user-activity user-activity--is-empty">{t`No matches found.`}</div>
        ) : null}
      </>
    )
  },
)

// Constant used in multiple places
const isTotalProjectedPercentageVisible = true
